import React, { useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";

/**
 * The shared dialog. This repo had no modal wrapper at all — MUI `Dialog` was
 * used directly in the Manage* pages and ScheduleHistory hand-rolled its own
 * overlay — so every new dialog meant re-solving the same problems. This one
 * handles them once:
 *
 *   - ESC closes, backdrop click closes (but a click that STARTED inside and
 *     ended on the backdrop does not — that is how text selection escapes a
 *     dialog and closes it by accident)
 *   - body scroll is locked while open, and restored to whatever it was
 *   - focus moves into the panel on open and returns to the trigger on close
 *   - the panel scrolls, not the page, so a long detail never pushes the
 *     header off screen
 */
const SIZES = {
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
};

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  badges,
  size = "lg",
  children,
  footer,
}) {
  const panelRef = useRef(null);
  const mouseDownTarget = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);

    // Move focus in so ESC and screen readers work without a click first.
    panelRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center sm:p-6"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onClick={(e) => {
        // Only a click that both started and ended on the backdrop closes.
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        className={`w-full ${SIZES[size] || SIZES.lg} my-auto flex max-h-[90vh] flex-col rounded-2xl bg-white shadow-2xl outline-none`}
      >
        <header className="flex items-start gap-4 border-b border-slate-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-bold text-slate-900">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">
                {subtitle}
              </p>
            )}
            {badges && <div className="mt-2 flex flex-wrap items-center gap-2">{badges}</div>}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <FiX size={18} />
          </button>
        </header>

        {/* The panel scrolls — not the page. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
